import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../assets/img/logo/buddha.png";
import { Eye, EyeOff } from "lucide-react";
import { API } from "../utils/axios";

const CreatePassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const automaticLoginTokenRef = React.useRef<string | null>(null);
  if (!automaticLoginTokenRef.current) {
    automaticLoginTokenRef.current = generateRandomText(20);
  }
  const merchant_portal =
    global.config.ROOTURL.prod === "https://lastminutewellness.ca/api"
      ? `https://my.lastminutewellness.ca/index.php?route=account/login/adminLogin&token=${automaticLoginTokenRef.current}`
      : `https://demo.lastminutewellness.ca/index.php?route=account/login/adminLogin&token=${automaticLoginTokenRef.current}`;
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPasswordCreated, setIsPasswordCreated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const backgroundLogoStyle: React.CSSProperties = {
    backgroundImage: `url(${logo})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "24%",
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-red-600 text-lg font-medium">
          Invalid or missing token.
        </p>
      </div>
    );
  }

  function generateRandomText(length = 10) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }

    setErrorMessage("");

    try {
      setLoading(true);

      const { data } = await API.post(
        `/business/create-password`,
        { token, password, automaticLoginToken:automaticLoginTokenRef.current }
      );

      toast.success(data.message);
      setIsPasswordCreated(true);
    } catch (error) {
      toast.error("Something went wrong");
      setLoading(false);
    }
  };

  return (
    <>
      {isPasswordCreated ? (
        <div className="min-h-screen flex text-center justify-center items-center">
          <div
            className="absolute !top-[0px] left-0 right-0 bottom-0 z-0 opacity-5 pointer-events-none"
            style={backgroundLogoStyle}
          ></div>
          <div>
            <p>Your password has been set successfully.</p>

            <div className="my-6">
              <p>
                You can now access your account using the portal link below:
              </p>

              <p>
                <a
                  href={merchant_portal}
                  target="_blank"
                  className="text-blue-400 underline"
                >
                  {merchant_portal}
                </a>
              </p>
            </div>

            <p>
              Thank you,
              <br />
              LastMinuteWellness Team
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
          <div className="w-full max-w-md bg-white shadow-md rounded-xl p-8">
            <h1 className="text-2xl font-semibold text-center mb-2">
              Create Your Password
            </h1>
            <p className="text-gray-600 text-center mb-6">
              Set a password to activate your business account.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password */}
              <div className="relative">
                <label className="block font-medium mb-1">New Password</label>

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full border rounded-lg px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />

                  {/* Eye Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-600 hover:text-gray-800"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <p className="text-red-500 text-sm text-center">
                  {errorMessage}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700 transition disabled:opacity-50"
              >
                {loading ? "Creating..." : "Create Password"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePassword;
